import React, { useEffect, useState } from 'react';
import { Home, Briefcase, MapPin, Plus, Trash2, Check, Loader2, Star, ContactRound } from 'lucide-react';
import { addressesApi } from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * SavedAddressPicker — dropdown of the user's saved addresses.
 *  - Blank by default: customer either picks a saved row OR types a new
 *    address in the field below (managed by parent).
 *  - "Set as default" quick action after a fresh entry (parent-controlled).
 *  - Delete inline (small trash icon).
 *
 * Emits `onPick(addr)` when a saved address is chosen (parent hydrates its
 * address + lat/lng state). Emits `onClear()` when "New address" is picked.
 */
const iconFor = (label = '') => {
  const l = label.toLowerCase();
  if (l.includes('home')) return Home;
  if (l.includes('work') || l.includes('office')) return Briefcase;
  return MapPin;
};

const SavedAddressPicker = ({ selectedId = null, onPick, onClear, refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await addressesApi.list();
      setRows(data || []);
    } catch (_e) {
      // Silently degrade: unauthenticated users get an empty list.
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const remove = async (id) => {
    try {
      await addressesApi.remove(id);
      toast.success('Address removed');
      if (selectedId === id) onClear?.();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center justify-center text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Loading saved addresses…
      </div>
    );
  }
  if (rows.length === 0) return (
    <ContactImportButton onImported={load} />
  );

  return (
    <div className="space-y-2" data-testid="saved-address-picker">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
          Saved addresses · up to 3
        </div>
        <ContactImportButton inline onImported={load} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rows.map((a) => {
          const Icon = iconFor(a.label);
          const active = selectedId === a.id;
          return (
            <div key={a.id} className={`rounded-xl border p-3 flex items-start gap-2.5 transition ${active ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
              <button
                type="button"
                onClick={() => onPick?.(a)}
                className="flex-1 text-left flex items-start gap-2.5"
                data-testid={`saved-address-${a.id}`}
              >
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-none ${active ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                    {a.label}
                    {a.is_default && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                        <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" /> Default
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{a.address}</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => remove(a.id)}
                className="text-slate-400 hover:text-red-600 p-1"
                data-testid={`saved-address-remove-${a.id}`}
                aria-label="Remove saved address"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
      {selectedId && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-emerald-700 font-semibold hover:underline flex items-center gap-1"
          data-testid="saved-address-clear"
        >
          <Plus className="h-3 w-3" /> Use a different address instead
        </button>
      )}
    </div>
  );
};

/**
 * ContactImportButton — one-tap import from the phone's Contact Picker (Chrome
 * on Android / newer iOS Safari). Falls back to a helpful tooltip on desktop
 * browsers that don't support the Contact Picker API.
 */
export const ContactImportButton = ({ onImported, inline = false }) => {
  const supported = typeof navigator !== 'undefined' && navigator.contacts && typeof navigator.contacts.select === 'function';
  const [busy, setBusy] = useState(false);

  const importAddresses = async () => {
    if (!supported) {
      toast.info("Your browser doesn't support one-tap contact import — try on your phone. You can still add addresses manually below.");
      return;
    }
    setBusy(true);
    try {
      const props = ['name', 'address'];
      const opts = { multiple: true };
      const contacts = await navigator.contacts.select(props, opts);
      if (!contacts?.length) return;
      let added = 0;
      for (const c of contacts) {
        for (const rawAddr of (c.address || [])) {
          const line = [
            (rawAddr.addressLine || []).join(', '),
            rawAddr.city, rawAddr.region, rawAddr.postalCode, rawAddr.country,
          ].filter(Boolean).join(', ');
          if (!line) continue;
          // Best-effort geocode via Nominatim (respects Ottawa/Ibadan geofence).
          try {
            const q = encodeURIComponent(line);
            const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${q}`, {
              headers: { Accept: 'application/json' },
            });
            const geo = await geoRes.json();
            if (!geo?.length) { continue; }
            const g = geo[0];
            await addressesApi.add({
              label: (c.name && c.name[0]) || 'Imported',
              address: line,
              lat: parseFloat(g.lat),
              lng: parseFloat(g.lon),
              is_default: false,
            });
            added++;
          } catch (_e) { /* skip unreachable / out-of-area */ }
        }
      }
      if (added) {
        toast.success(`Imported ${added} address${added > 1 ? 'es' : ''} from your contacts.`);
        onImported?.();
      } else {
        toast.info('No importable Ottawa/Ibadan addresses found in your picks.');
      }
    } catch (e) {
      if (e?.name === 'AbortError' || e?.name === 'SecurityError') return;
      toast.error(e?.message || 'Import failed');
    } finally { setBusy(false); }
  };

  if (inline) {
    return (
      <button
        type="button"
        onClick={importAddresses}
        disabled={busy || !supported}
        className="text-xs font-semibold text-emerald-700 hover:underline flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="import-contact-address-btn-inline"
        title={supported ? 'Import from your phone contacts' : 'Available on mobile Chrome / Safari only'}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ContactRound className="h-3.5 w-3.5" />}
        Import from contacts
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 flex items-center gap-3" data-testid="import-contact-empty">
      <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none">
        <ContactRound className="h-5 w-5" />
      </div>
      <div className="flex-1 text-sm text-slate-600">
        <div className="font-semibold text-slate-900">No saved addresses yet</div>
        <div className="text-xs">Import your Home and Work addresses in one tap from your phone contacts.</div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={importAddresses}
        disabled={busy}
        data-testid="import-contact-address-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Import'}
      </Button>
    </div>
  );
};

/**
 * SavedAddressSaveButton — small "Save this address for later" affordance
 * that Checkout shows after the customer has typed a fresh address. Pass the
 * current address + lat/lng and a label, and it POSTs to /me/addresses.
 */
export const SavedAddressSaveButton = ({ address, lat, lng, disabled, defaultLabel = 'Home', onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [label, setLabel] = useState(defaultLabel);
  const save = async () => {
    setBusy(true);
    try {
      const addr = await addressesApi.add({ label: label.trim() || 'Address', address, lat, lng, is_default: false });
      setSaved(true);
      toast.success(`Saved as "${addr.label}"`);
      onSaved?.(addr);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save address'); }
    finally { setBusy(false); }
  };

  if (saved) {
    return (
      <div className="text-xs text-emerald-700 font-semibold flex items-center gap-1" data-testid="saved-address-saved-ack">
        <Check className="h-3.5 w-3.5" /> Address saved
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid="saved-address-save-row">
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value.slice(0, 40))}
        placeholder="Label (Home, Work…)"
        className="text-xs px-2 py-1 rounded border border-slate-200 w-32"
        data-testid="saved-address-label-input"
      />
      <Button
        size="sm"
        variant="outline"
        onClick={save}
        disabled={busy || disabled || !address || lat == null || lng == null}
        className="text-xs h-7"
        data-testid="saved-address-save-btn"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3 w-3 mr-1" />Save address</>}
      </Button>
    </div>
  );
};

export default SavedAddressPicker;
